/**
 * OH3 利用度トラッキングシステム
 * ボタンクリック、レイヤー選択などのイベントを記録し、サーバーに送信
 */

export class UsageTracker {
  constructor(apiEndpoint, store = null) {
    this.apiEndpoint = apiEndpoint || process.env.VUE_APP_USAGE_API_ENDPOINT || '/oh3/php/usageApiProxy.php?path=events'
    this.sessionId = this.getOrCreateSessionId()
    this.events = []
    this.maxBatchSize = 50
    this.flushInterval = 60000 // 1分ごとに送信
    this.isEnabled = true
    this.store = store // Vuex store

    // アプリバージョンを取得
    this.appVersion = this.getAppVersion()

    // 定期的な送信
    this.intervalId = setInterval(() => this.flush(), this.flushInterval)

    // ページ離脱時に送信
    window.addEventListener('beforeunload', () => {
      this.flushSync()
    })
  }

  /**
   * UIDを簡易ハッシュ化（個人特定不可にする）
   */
  _hashUid(uid) {
    let hash = 0
    for (let i = 0; i < uid.length; i++) {
      const char = uid.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash |= 0 // 32bit整数に変換
    }
    return 'u_' + Math.abs(hash).toString(36)
  }

  /**
   * セッションIDを取得または生成
   * 同日中は同じセッションIDを使用（DAU計測を正確にするため）
   */
  getOrCreateSessionId() {
    const today = new Date().toISOString().split('T')[0] // YYYY-MM-DD
    const storageKey = 'oh3_session_id'
    const storageDateKey = 'oh3_session_date'

    try {
      const storedDate = localStorage.getItem(storageDateKey)
      const storedId = localStorage.getItem(storageKey)

      // 同日で既存のsession_idがあれば再利用
      if (storedDate === today && storedId) {
        return storedId
      }

      // 新しい日 or 初回: 新規生成して保存
      const newId = this.generateSessionId()
      localStorage.setItem(storageKey, newId)
      localStorage.setItem(storageDateKey, today)
      return newId
    } catch (e) {
      // localStorage使用不可の場合はフォールバック
      return this.generateSessionId()
    }
  }

  /**
   * セッションIDを生成（UUID v4）
   */
  generateSessionId() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID()
    }
    // フォールバック
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0
      const v = c === 'x' ? r : (r & 0x3) | 0x8
      return v.toString(16)
    })
  }

  /**
   * アプリバージョンを取得
   */
  getAppVersion() {
    // package.jsonのバージョンを取得（ビルド時に埋め込まれている場合）
    if (window.__APP_VERSION__) {
      return window.__APP_VERSION__
    }
    // localStorageから取得（main.jsで保存されている可能性）
    try {
      const stored = localStorage.getItem('oh3_client_version')
      if (stored) return stored
    } catch (e) {}
    return '1.0.0'
  }

  /**
   * イベントを記録
   * @param {string} category - イベントカテゴリ ('button', 'layer', 'feature')
   * @param {string} action - アクション ('click', 'select', 'toggle')
   * @param {string} label - ラベル (ボタンID、レイヤーIDなど)
   * @param {object} value - 追加情報（オプション）
   */
  trackEvent(category, action, label = '', value = null) {
    if (!this.isEnabled) return

    // Vuex storeからユーザー情報を取得
    // userId: バックエンド (server.js) が読むフィールド名。
    // ハッシュ化済み (u_xxxxx) の匿名ID を送る。
    let userId = null
    let nickname = null
    if (this.store && this.store.state) {
      const uid = this.store.state.userId || null
      if (uid) {
        // UIDをハッシュ化して匿名化（個人特定不可）
        userId = this._hashUid(uid)
      }
      nickname = this.store.state.myNickname || null
    }

    const event = {
      sessionId: this.sessionId,
      category,
      action,
      label,
      value,
      timestamp: Date.now(),
      appVersion: this.appVersion,
      userId,
      // 後方互換のため古いバックエンドが読むかも知れない anonUserId も送る
      anonUserId: userId,
      nickname,
    }

    this.events.push(event)

    // localStorageにもバックアップ（送信失敗時のため）
    this.saveToLocalStorage(event)

    // バッチサイズに達したら即座に送信
    if (this.events.length >= this.maxBatchSize) {
      this.flush()
    }
  }

  /**
   * イベントをサーバーに送信（非同期）
   */
  async flush() {
    if (this.events.length === 0) return

    const eventsToSend = [...this.events]
    this.events = []

    try {
      const response = await fetch(this.apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ events: eventsToSend }),
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const result = await response.json()
    } catch (error) {
      console.error('[UsageTracker] Failed to send events:', error)
      // エラー時はイベントを戻す（次回リトライ）
      this.events = [...eventsToSend, ...this.events].slice(0, 100) // 最大100件まで保持
    }
  }

  /**
   * 同期的に送信（ページ離脱時用）
   */
  flushSync() {
    if (this.events.length === 0) return

    try {
      // Navigator.sendBeacon を使用（非同期だが、ページ離脱時も送信される）
      if (navigator.sendBeacon) {
        const blob = new Blob([JSON.stringify({ events: this.events })], {
          type: 'application/json',
        })
        navigator.sendBeacon(this.apiEndpoint, blob)
      } else {
        // フォールバック: 同期XHR（非推奨だが、ページ離脱時に必要）
        const xhr = new XMLHttpRequest()
        xhr.open('POST', this.apiEndpoint, false) // 同期モード
        xhr.setRequestHeader('Content-Type', 'application/json')
        xhr.send(JSON.stringify({ events: this.events }))
      }
      this.events = []
    } catch (error) {
      console.error('[UsageTracker] Failed to send events on unload:', error)
    }
  }

  /**
   * localStorageにバックアップ保存
   */
  saveToLocalStorage(event) {
    try {
      const key = 'oh3_usage_events_backup'
      const existing = JSON.parse(localStorage.getItem(key) || '[]')
      const combined = [...existing, event].slice(-500) // 最新500件のみ保持
      localStorage.setItem(key, JSON.stringify(combined))
    } catch (error) {
      // localStorageが満杯の場合などはエラーを無視
      console.warn('[UsageTracker] Failed to save to localStorage:', error)
    }
  }

  /**
   * トラッキングを無効化
   */
  disable() {
    this.isEnabled = false
  }

  /**
   * トラッキングを有効化
   */
  enable() {
    this.isEnabled = true
  }

  /**
   * クリーンアップ
   */
  destroy() {
    clearInterval(this.intervalId)
    this.flush()
  }
}

// シングルトンインスタンスをエクスポート
let trackerInstance = null

export function getUsageTracker(apiEndpoint, store = null) {
  if (!trackerInstance) {
    trackerInstance = new UsageTracker(apiEndpoint, store)
  } else if (store && !trackerInstance.store) {
    // storeが後から渡された場合は更新
    trackerInstance.store = store
  }
  return trackerInstance
}

export default UsageTracker
